// ============================================================================
// JARVISTRAVEL - RECEIPTS API
// Version: 1.0.0
// Receipt-to-itinerary confirmation API calls
// JT-JOUR-002: Receipt-to-itinerary confirmation
// ============================================================================

import { api, isDemoSession, isTripLocked } from "./client";
import { warnOnce } from '@/app/utils/observability';
import { reportApiFailure } from './failure';
import { toLocalYmd } from '@/app/utils/dateOnly';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ManualReceipt {
  merchant: string;
  amount: number;
  currency?: string;
  /**
   * RFC3339, genuinely — this is a REQUEST body, and the server enforces it:
   * trip-core parses this field with time.Parse(time.RFC3339, ...) and returns
   * 400 "invalid date_time: must be RFC3339 format" for anything else
   * (services/trip-core/internal/receipts/service.go). An earlier revision of
   * this comment declared the field local-unzoned; that described the
   * ReceiptEvidence emitters below, which are a different type, and following
   * it here would have produced a hard 400 on the first wired-up caller
   * (JAR-703 review, round 2).
   *
   * Build it with an explicit local OFFSET (2026-07-28T23:30:00+09:00), not
   * with toISOString(): a UTC-converted instant loses the local calendar day
   * the moment anything reduces it to a date without applying the offset.
   */
  date_time: string;
  location_hint?: string;
}

export interface MatchOption {
  itinerary_item_id: string;
  item_title: string;
  item_date?: string;
  confidence: "high" | "medium" | "low";
  match_reason: string;
  budget_delta: number;
}

export interface ReceiptMatchResult {
  receipt_id: string;
  best_match?: MatchOption;
  other_matches?: MatchOption[];
  suggestions?: string[];
}

/** A line item extracted from the receipt — what you actually had. */
export interface ReceiptItem {
  name: string;
  price?: number;
}

export interface ReceiptEvidence {
  id: string;
  trip_id: string;
  journal_entry_id?: string;
  merchant: string;
  amount: number;
  currency: string;
  /**
   * MIXED-SOURCE — the shape depends on who made the value, and consumers must
   * not assume either one (JAR-703 review, round 2):
   *
   * - Server-supplied (listReceipts, addReceipt responses): Go marshals its
   *   time.Time as ZONED RFC3339 ("...T14:00:00Z"). Slicing that to ten
   *   characters yields the UTC day, which is the previous day for an evening
   *   receipt anywhere east of Greenwich — the JAR-610 L3 bug.
   * - Locally constructed (mock scan, OCR fallback, demo data): local and
   *   UNZONED ("YYYY-MM-DDTHH:MM"), where the leading ten characters ARE the
   *   local day.
   *
   * Reduce to a calendar day through receiptLocalDay(), never by slicing.
   */
  date_time: string;
  location_hint?: string;
  confidence: number;
  match_type?: string;
  matched_item_id?: string;
  user_confirmed: boolean;
  created_at: string;
  /** Extracted line items, when the scan could read them. */
  items?: ReceiptItem[];
}

// ---------------------------------------------------------------------------
// API Calls
// ---------------------------------------------------------------------------

/** Enter a receipt manually — system matches it to itinerary items */
export async function addReceipt(tripId: string, data: ManualReceipt): Promise<{
  receipt: ReceiptEvidence;
  match: ReceiptMatchResult;
} | null> {
  const result = await api.post<{
    receipt: ReceiptEvidence;
    match: ReceiptMatchResult;
  }>(`/trips/${tripId}/receipts`, data);
  if (result.error) {
    reportApiFailure("[receipts] add failed:", result.error);
    return null;
  }
  return result.data;
}

/** Confirm a receipt match — links receipt to itinerary item */
export async function confirmReceipt(
  tripId: string,
  receiptId: string,
  itemId: string,
  action: string = "confirm_meal"
): Promise<boolean> {
  const result = await api.post<{ status: string }>(
    `/trips/${tripId}/receipts/${receiptId}/confirm`,
    { item_id: itemId, action }
  );
  if (result.error) {
    reportApiFailure("[receipts] confirm failed:", result.error);
    return false;
  }
  return result.data?.status === "confirmed";
}

/** Dismiss a receipt — mark as irrelevant */
export async function dismissReceipt(tripId: string, receiptId: string): Promise<boolean> {
  const result = await api.post<{ status: string }>(
    `/trips/${tripId}/receipts/${receiptId}/dismiss`
  );
  if (result.error) {
    reportApiFailure("[receipts] dismiss failed:", result.error);
    return false;
  }
  return result.data?.status === "dismissed";
}

/** List all receipts for a trip */
export async function listReceipts(tripId: string): Promise<ReceiptEvidence[]> {
  const result = await api.get<{ receipts: ReceiptEvidence[] }>(
    `/trips/${tripId}/receipts`
  );
  if (result.error) {
    warnOnce("[receipts] list failed:", result.error);
    return [];
  }
  return result.data?.receipts || [];
}

// ---------------------------------------------------------------------------
// Receipt scanning (photo capture)
// ---------------------------------------------------------------------------

/** An itinerary item the extracted receipt can be matched against (mock mode). */
export interface ScanCandidate {
  id: string;
  title: string;
  date?: string;
}

export interface ScanInput {
  fileName?: string;
  /** The captured image, uploaded to the live scan endpoint as multipart. */
  file?: File;
  candidates?: ScanCandidate[];
  /** Expected currency for mock extraction. Defaults to "EUR" when omitted. */
  currency?: string;
}

/** The live scan endpoint's actual response — extraction only, with no id and no
 *  itinerary match (unlike the mock, which fabricates both). Mirrors core's
 *  ScanReceiptResponse (receipts/models.go). */
interface ScanReceiptResponse {
  merchant: string;
  total: number;
  currency: string;
  date: string;
  time?: string;
  line_items?: { description: string; quantity?: number; unit_price?: number; total_price?: number }[];
  raw_text?: string;
}

/** The sample's match, for the demo only. Its receipt and its itinerary are both
 *  built here, so the correspondence is a property of the fixture rather than a
 *  claim about anyone's data. A live scan uses matchByMerchant (JAR-2646). */
function sampleMatch(receiptId: string, candidate?: ScanCandidate): ReceiptMatchResult {
  return candidate
    ? {
        receipt_id: receiptId,
        best_match: {
          itinerary_item_id: candidate.id,
          item_title: candidate.title,
          item_date: candidate.date,
          confidence: "high",
          match_reason: "Merchant and amount line up with this stop on your plan",
          budget_delta: 0,
        },
      }
    : { receipt_id: receiptId, suggestions: [NO_MATCH_SUGGESTION] };
}

const NO_MATCH_SUGGESTION = "No itinerary match: add it as a cash expense";

/** Words that identify nothing: every other restaurant on the trip has them. */
const GENERIC_MERCHANT_WORDS = new Set([
  "the", "and", "cafe", "caffe", "bar", "restaurant", "ristorante", "trattoria",
  "osteria", "pizzeria", "hotel", "inn", "shop", "store", "market", "visit",
  "tour", "museum", "gallery", "park", "ltd", "llc", "inc", "srl", "spa",
]);

/** A merchant name reduced to the words that could identify a place. */
function identifyingWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .split(" ")
    .filter((word) => word.length > 2 && !GENERIC_MERCHANT_WORDS.has(word));
}

/**
 * The stop a scanned receipt belongs to, by comparing what was read against
 * what is on the plan (JAR-2646).
 *
 * The version this replaces took `candidates[0]` — the FIRST restaurant,
 * attraction or shopping stop on the trip, by position and nothing else — and
 * reported it as a high-confidence match whose reason read "Merchant and amount
 * line up with this stop on your plan". Nothing had been lined up. On Brent's
 * San Diego trip that attached an invented Roman dinner to Balboa Park and said
 * so as a fact.
 *
 * So it compares, and says only what it compared: the merchant's name against
 * the stop's. `medium`, never `high`, because a name is one of the two things
 * that sentence used to claim. No comparison lands, no match is offered, and
 * the person picks the stop themselves.
 */
export function matchByMerchant(
  receiptId: string,
  merchant: string,
  candidates: ScanCandidate[] = [],
): ReceiptMatchResult {
  const words = identifyingWords(merchant);
  const hit = words.length === 0
    ? undefined
    : candidates.find((candidate) => {
        const title = identifyingWords(candidate.title);
        return words.some((word) => title.includes(word));
      });
  if (!hit) return { receipt_id: receiptId, suggestions: [NO_MATCH_SUGGESTION] };
  return {
    receipt_id: receiptId,
    best_match: {
      itinerary_item_id: hit.id,
      item_title: hit.title,
      item_date: hit.date,
      confidence: "medium",
      match_reason: `The name on the receipt matches ${hit.title} on your plan`,
      budget_delta: 0,
    },
  };
}

const MOCK_RECEIPTS: { merchant: string; amount: number; items: ReceiptItem[] }[] = [
  {
    merchant: "Trattoria Vecchia Roma", amount: 64,
    items: [
      { name: "Bucatini all'amatriciana", price: 18 },
      { name: "Saltimbocca alla romana", price: 24 },
      { name: "House red (½L)", price: 14 },
      { name: "Tiramisù", price: 8 },
    ],
  },
  {
    merchant: "Caffè Sant'Eustachio", amount: 11.5,
    items: [
      { name: "Gran caffè ×2", price: 7 },
      { name: "Cornetto", price: 4.5 },
    ],
  },
  {
    merchant: "Gelateria Giolitti", amount: 8,
    items: [
      { name: "Gelato (pistachio & stracciatella)", price: 5 },
      { name: "Cono grande", price: 3 },
    ],
  },
  {
    merchant: "Pizzeria La Montecarlo", amount: 31.5,
    items: [
      { name: "Margherita", price: 12 },
      { name: "Diavola", price: 14 },
      { name: "Acqua frizzante", price: 5.5 },
    ],
  },
  {
    merchant: "Mercato Centrale", amount: 24,
    items: [
      { name: "Porchetta panino", price: 9 },
      { name: "Supplì ×2", price: 8 },
      { name: "Spremuta d'arancia", price: 7 },
    ],
  },
];

/** Deterministic pick from an array based on a stable string seed. */
function seededPick<T>(items: readonly T[], seed: string): T {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return items[Math.abs(hash) % items.length];
}

/** Produce a plausible extraction locally so the capture → confirm → catalog
 *  flow is exercisable before the backend scan endpoint exists. */
function mockScan(tripId: string, input: ScanInput): { receipt: ReceiptEvidence; match: ReceiptMatchResult; isMock: boolean } {
  const pick = seededPick(MOCK_RECEIPTS, tripId);
  const candidate = input.candidates && input.candidates.length > 0
    ? input.candidates[0]
    : undefined;
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  // date_time is consumed as a CALENDAR date, not an instant: BudgetManager
  // slices it to 10 characters to reconcile a receipt against a budget line. A
  // UTC timestamp sliced that way gives the wrong day for anyone east of
  // Greenwich after local midnight — a receipt filed at 01:00 in Tokyo is
  // 16:00 the previous day in UTC, so it reconciles against yesterday
  // (JAR-610 L3). Local, and the same unzoned shape the OCR branch produces.
  const localDateTime = localReceiptDateTime();
  const receipt: ReceiptEvidence = {
    id,
    trip_id: tripId,
    merchant: pick.merchant,
    amount: pick.amount,
    currency: input.currency ?? "EUR",
    date_time: localDateTime,
    location_hint: candidate?.title,
    confidence: candidate ? 0.92 : 0.5,
    match_type: candidate ? "auto" : "unmatched",
    user_confirmed: false,
    created_at: now,
    items: pick.items,
  };
  return { receipt, match: sampleMatch(id, candidate), isMock: true };
}

/**
 * Scan a photographed receipt → extracted evidence + itinerary match.
 *
 * The demo extracts locally, where every value on screen is a sample and the
 * app says so. **Everywhere else, a scan that cannot run FAILS** (JAR-2646).
 *
 * It used to fall through to that same local extraction, which meant a scan
 * the backend refused produced a merchant and an amount out of a fixture list
 * and showed them as a reading of the person's own receipt. Brent photographed
 * a receipt in San Diego and was told he had spent EUR 64 at Trattoria Vecchia
 * Roma. The only disclosure was one amber line reading "Using local estimate",
 * which says the quality is lower, not that none of it is real.
 *
 * Inventing a number in the feature that tracks someone's money is worse than
 * having no feature, so there is no longer a path from a failed scan to a
 * receipt on screen.
 */
/** Thrown when a live scan cannot run. On the JarvisTab's safe-message list, so
 *  it reaches the screen rather than being flattened to "Failed to scan". */
export const SCAN_UNAVAILABLE = "Scan API unavailable";

export async function scanReceipt(
  tripId: string,
  input: ScanInput,
): Promise<{ receipt: ReceiptEvidence; match: ReceiptMatchResult; isMock: boolean }> {
  // Live scan needs the actual image as multipart/form-data — the endpoint reads
  // an "image" field and returns extraction (ScanReceiptResponse), NOT the
  // {receipt, match} the old JSON call declared (it posted to a multipart-only
  // route and could never have worked). Without a file we can't scan live, so
  // fall through to the local mock (JAR-449).
  if (!isDemoSession()) {
    // No file, no scan. The caller always has one; a missing file is a caller
    // bug, and a fixture is not the answer to it.
    if (!input.file) throw new Error(SCAN_UNAVAILABLE);
    const form = new FormData();
    form.append("image", input.file);
    const result = await api.post<ScanReceiptResponse>(`/trips/${tripId}/receipts/scan`, form);
    if (!result.error && result.data) {
      const res = result.data;
      const candidate = input.candidates?.[0];
      const id = crypto.randomUUID();
      const receipt: ReceiptEvidence = {
        id,
        trip_id: tripId,
        merchant: res.merchant,
        amount: res.total,
        currency: res.currency || input.currency || "EUR",
        date_time: res.date ? `${res.date}T${res.time || "00:00"}` : localReceiptDateTime(),
        location_hint: candidate?.title,
        confidence: candidate ? 0.92 : 0.5,
        match_type: candidate ? "auto" : "unmatched",
        user_confirmed: false,
        created_at: new Date().toISOString(),
        items: (res.line_items ?? []).map((li) => ({ name: li.description, price: li.total_price || li.unit_price })),
      };
      return { receipt, match: matchByMerchant(id, res.merchant, input.candidates), isMock: false };
    }
    // A trip whose expenses have closed says so in core's own words (JAR-1864).
    if (isTripLocked(result.error)) throw result.error;
    reportApiFailure("[receipts] scan endpoint unavailable");
    throw new Error(SCAN_UNAVAILABLE);
  }
  return mockScan(tripId, input);
}

/**
 * "YYYY-MM-DDTHH:MM" in the user's own timezone.
 *
 * Matches the shape the OCR branch produces from the receipt's printed date and
 * time — unzoned, local, and safe to slice to a calendar date. `created_at`
 * deliberately stays a UTC instant: that one records when the row was written,
 * which is a moment in time rather than a day on a calendar.
 */
export function localReceiptDateTime(d = new Date()): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${toLocalYmd(d)}T${hh}:${mm}`;
}

/**
 * The calendar day a receipt belongs to, for reconciliation and day matching.
 *
 * date_time is mixed-source (see ReceiptEvidence): a server value is a zoned
 * RFC3339 instant, and slicing IT to ten characters gives the UTC day — the
 * previous day for an evening receipt anywhere east of Greenwich, which is
 * exactly the JAR-610 L3 misfile this module exists to prevent. A zoned value
 * is therefore reduced through Date, landing on the viewer's local day; an
 * unzoned local value keeps its literal leading date, which needs no clock at
 * all. Invalid input degrades to '' rather than a date-shaped wrong answer.
 */
export function receiptLocalDay(dateTime: string): string {
  const v = dateTime || '';
  // Z or a numeric offset at the end marks a zoned instant.
  if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(v)) {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : toLocalYmd(d);
  }
  return v.slice(0, 10);
}
